import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuth } from '../context/AuthContext';
import { 
  GraduationCap, 
  Plus, 
  Calendar as CalendarIcon, 
  Trash2, 
  Filter,
  Inbox,
  FileText,
  Shirt,
  Clock,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Send,
  Loader2,
  ChevronRight,
  User,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format, isToday, isTomorrow, isPast, addDays } from 'date-fns';
import WhatsAppPlaceholder from '../components/WhatsAppPlaceholder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'notice', label: 'Notice', color: 'bg-blue-100 text-blue-700' },
  { value: 'permission', label: 'Permission Slip', color: 'bg-purple-100 text-purple-700' },
  { value: 'uniform', label: 'Uniform', color: 'bg-pink-100 text-pink-700' },
  { value: 'event', label: 'Event', color: 'bg-green-100 text-green-700' },
  { value: 'health', label: 'Health', color: 'bg-red-100 text-red-700' },
  { value: 'supplies', label: 'Supplies', color: 'bg-orange-100 text-orange-700' },
  { value: 'fee', label: 'Fee/Payment', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

const getCategoryStyle = (category) => {
  const cat = categories.find(c => c.value === category);
  return cat ? cat.color : 'bg-gray-100 text-gray-700';
};

// Assignee picker sentinel (Select cannot hold an empty value)
const UNASSIGNED = '__unassigned__';

const SchoolTrackerPage = () => {
  const { user } = useAuth();
  const canAssign = ['parent', 'partner'].includes(user?.family_role || 'parent');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterChild, setFilterChild] = useState('all');
  const [newTask, setNewTask] = useState({
    child_name: '',
    task: '',
    due_date: null,
    category: 'general',
    notes: '',
    assigned_to: UNASSIGNED
  });

  // AI States
  const [extractText, setExtractText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractedTasks, setExtractedTasks] = useState([]);
  const [extractCallId, setExtractCallId] = useState(null);
  const [editedExtractedTasks, setEditedExtractedTasks] = useState([]);
  const [uniformEvent, setUniformEvent] = useState('');
  const [generatingUniform, setGeneratingUniform] = useState(false);
  const [uniformChecklist, setUniformChecklist] = useState([]);
  const [uniformCallId, setUniformCallId] = useState(null);
  const [uniformLowConfidence, setUniformLowConfidence] = useState(false);
  const [generatingPrep, setGeneratingPrep] = useState(false);
  const [tomorrowPrep, setTomorrowPrep] = useState([]);
  const [tomorrowPrepCallId, setTomorrowPrepCallId] = useState(null);
  const [editedTomorrowPrep, setEditedTomorrowPrep] = useState([]);
  const [reminderText, setReminderText] = useState('');
  const [reminderCallId, setReminderCallId] = useState(null);
  const [generatingReminder, setGeneratingReminder] = useState(false);
  
  // Phase 1 Step 3: Image upload state for School Inbox
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageChildName, setImageChildName] = useState('');
  const [imageExtractedItems, setImageExtractedItems] = useState([]);
  const [imageExtractCallId, setImageExtractCallId] = useState(null);
  const [imageItemAssignees, setImageItemAssignees] = useState({});
  
  // E2.6-A: Forms & Slips State
  const [schoolForms, setSchoolForms] = useState([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    child_name: '',
    form_title: '',
    amount_rp: '',
    due_date: null
  });
  
  // E2.6-C: Timeline State  
  const [timelineData, setTimelineData] = useState(null);
  const [timelineChild, setTimelineChild] = useState('');

  // HITL confirmation helper
  const confirmAIAction = async (callId, userAction, editDelta = []) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Cleanup on unmount - confirm ignored for pending AI calls
  useEffect(() => {
    return () => {
      if (extractCallId && extractedTasks.length > 0) {
        confirmAIAction(extractCallId, 'ignored');
      }
      if (tomorrowPrepCallId && tomorrowPrep.length > 0) {
        confirmAIAction(tomorrowPrepCallId, 'ignored');
      }
    };
  }, [extractCallId, tomorrowPrepCallId]);

  useEffect(() => {
    fetchTasks();
    fetchSchoolForms();
    fetchFamilyMembers();
  }, []);

  // Adults who can be assigned a school task
  const fetchFamilyMembers = async () => {
    try {
      const response = await axios.get(`${API}/family/roles`);
      setFamilyMembers((response.data?.members || []).filter(m => m.name));
    } catch (error) {
      console.error('Error fetching family members:', error);
    }
  };

  const assignTask = async (taskId, value) => {
    try {
      await axios.put(`${API}/school-tasks/${taskId}/assign`, {
        assigned_to: value === UNASSIGNED ? null : value
      });
      toast.success(value === UNASSIGNED ? 'Set to unassigned' : `Assigned to ${value}`);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to assign task');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/school-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  // E2.6-A: Fetch school forms
  const fetchSchoolForms = async () => {
    try {
      const response = await axios.get(`${API}/school/forms`);
      setSchoolForms(response.data);
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  // E2.6-A: Add school form
  const handleAddForm = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        ...newForm,
        amount_rp: newForm.amount_rp ? parseInt(newForm.amount_rp) : null,
        due_date: newForm.due_date ? format(newForm.due_date, 'yyyy-MM-dd') : null
      };
      await axios.post(`${API}/school/forms`, formData);
      toast.success('Form added!');
      setFormDialogOpen(false);
      setNewForm({ child_name: '', form_title: '', amount_rp: '', due_date: null });
      fetchSchoolForms();
    } catch (error) {
      toast.error('Failed to add form');
    }
  };

  // E2.6-A: Update form status
  const updateFormStatus = async (formId, status) => {
    try {
      await axios.put(`${API}/school/forms/${formId}?form_status=${status}`);
      toast.success(`Form marked as ${status}`);
      fetchSchoolForms();
    } catch (error) {
      toast.error('Failed to update form');
    }
  };

  // E2.6-A: Delete form
  const deleteForm = async (formId) => {
    try {
      await axios.delete(`${API}/school/forms/${formId}`);
      toast.success('Form deleted');
      fetchSchoolForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  // E2.6-A: Generate AI reminder for outstanding forms
  const generateFormsReminder = async () => {
    const outstanding = schoolForms.filter(f => f.status === 'pending');
    if (outstanding.length === 0) {
      toast.info('No outstanding forms to remind about');
      return;
    }
    setGeneratingReminder(true);
    try {
      const response = await axios.post(`${API}/ai/school/reminder-write`, {
        outstanding_forms: outstanding
      });
      setReminderText(response.data.reminder_text);
      setReminderCallId(response.data.call_id);
    } catch (error) {
      toast.error('Failed to generate reminder');
    } finally {
      setGeneratingReminder(false);
    }
  };

  // E2.6-C: Fetch child timeline
  const fetchTimeline = async (childName) => {
    if (!childName) return;
    try {
      const response = await axios.get(`${API}/school/timeline/${encodeURIComponent(childName)}`);
      setTimelineData(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  // Watch timeline child changes
  useEffect(() => {
    if (timelineChild) {
      fetchTimeline(timelineChild);
    }
  }, [timelineChild]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...newTask,
        assigned_to: newTask.assigned_to === UNASSIGNED ? null : newTask.assigned_to,
        due_date: newTask.due_date ? format(newTask.due_date, 'yyyy-MM-dd') : null
      };
      await axios.post(`${API}/school-tasks`, taskData);
      toast.success('Task added!');
      setDialogOpen(false);
      setNewTask({ child_name: '', task: '', due_date: null, category: 'general', notes: '', assigned_to: UNASSIGNED });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const toggleStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API}/school-tasks/${task.id}`, { ...task, status: newStatus });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/school-tasks/${id}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Get unique children
  const children = [...new Set(tasks.map(t => t.child_name).filter(Boolean))];
  
  // Filter tasks by child
  const filteredTasks = filterChild === 'all' 
    ? tasks 
    : tasks.filter(t => t.child_name === filterChild);

  // Get overdue and upcoming tasks
  const overdueTasks = filteredTasks.filter(t => 
    t.status === 'pending' && t.due_date && isPast(new Date(t.due_date)) && !isToday(new Date(t.due_date))
  );
  const todayTasks = filteredTasks.filter(t => 
    t.status === 'pending' && t.due_date && isToday(new Date(t.due_date))
  );
  const tomorrowTasks = filteredTasks.filter(t => 
    t.status === 'pending' && t.due_date && isTomorrow(new Date(t.due_date))
  );
  const pendingTasks = filteredTasks.filter(t => t.status === 'pending');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');

  // AI: Extract tasks from school notice
  const handleExtractTasks = async () => {
    if (!extractText.trim()) {
      toast.error('Please paste a school notice first');
      return;
    }
    setExtracting(true);
    setExtractCallId(null);
    try {
      const response = await axios.post(`${API}/ai/extract-school-tasks`, {
        text: extractText
      });
      const tasks = response.data.tasks || [];
      setExtractedTasks(tasks);
      setEditedExtractedTasks(JSON.parse(JSON.stringify(tasks))); // Deep copy for editing
      setExtractCallId(response.data.call_id || null);
      toast.success(`Found ${tasks.length} tasks!`);
    } catch (error) {
      // Fallback: simulate extraction for demo
      const simulatedTasks = [
        { child_name: children[0] || 'Child', task: 'Bring permission slip for field trip', category: 'permission', due_date: format(addDays(new Date(), 3), 'yyyy-MM-dd') },
        { child_name: children[0] || 'Child', task: 'Wear sports uniform', category: 'uniform', due_date: format(addDays(new Date(), 1), 'yyyy-MM-dd') },
      ];
      setExtractedTasks(simulatedTasks);
      setEditedExtractedTasks(JSON.parse(JSON.stringify(simulatedTasks)));
      toast.success('Tasks extracted! (Demo mode)');
    } finally {
      setExtracting(false);
    }
  };

  // Phase 1 Step 3: Handle image file selection for School Inbox
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }
    
    // Convert to base64 for preview and API
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Phase 1 Step 3: Extract tasks from uploaded image
  const handleExtractFromImage = async () => {
    if (!imagePreview) {
      toast.error('Please select an image first');
      return;
    }
    
    setUploadingImage(true);
    setImageExtractedItems([]);
    
    try {
      const response = await axios.post(`${API}/school/inbox/extract-image`, {
        image_base64: imagePreview,
        child_name: imageChildName || (children[0] || 'Child')
      });
      
      if (response.data.error) {
        toast.error(response.data.error === 'not_school_document' 
          ? 'This doesn\'t appear to be a school document' 
          : response.data.error);
        return;
      }
      
      const items = response.data.items || [];
      setImageExtractedItems(items);
      setImageExtractCallId(response.data.call_id);
      
      if (items.length > 0) {
        toast.success(`Found ${items.length} items!`);
      } else {
        toast.info('No tasks found in the document');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to process file';
      toast.error(errorMsg);
    } finally {
      setUploadingImage(false);
    }
  };

  // Phase 1 Step 3: Save extracted image item as school task
  const saveImageExtractedItem = async (item, index) => {
    try {
      const assignee = imageItemAssignees[index];
      await axios.post(`${API}/school-tasks`, {
        child_name: item.child_name || imageChildName || 'Child',
        task: item.item_text,
        due_date: item.due_date,
        category: item.category || 'general',
        assigned_to: !assignee || assignee === UNASSIGNED ? null : assignee
      });
      toast.success('Task saved!');
      
      // Remove from list
      setImageExtractedItems(prev => prev.filter((_, i) => i !== index));
      
      // If last item, confirm AI action
      if (imageExtractedItems.length === 1) {
        await confirmAIAction(imageExtractCallId, 'accepted');
        setImageExtractCallId(null);
      }
      
      fetchTasks();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  // Phase 1 Step 3: Confirm all image extracted items
  const handleSaveAllImageItems = async () => {
    for (let i = 0; i < imageExtractedItems.length; i++) {
      const item = imageExtractedItems[i];
      const assignee = imageItemAssignees[i];
      try {
        await axios.post(`${API}/school-tasks`, {
          child_name: item.child_name || imageChildName || 'Child',
          task: item.item_text,
          due_date: item.due_date,
          category: item.category || 'general',
          assigned_to: !assignee || assignee === UNASSIGNED ? null : assignee
        });
      } catch (error) {
        console.error('Failed to save task:', error);
      }
    }
    
    await confirmAIAction(imageExtractCallId, 'accepted');
    toast.success(`${imageExtractedItems.length} tasks saved!`);
    setImageExtractedItems([]);
    setImageItemAssignees({});
    setImageExtractCallId(null);
    setImagePreview(null);
    fetchTasks();
  };

  // Phase 1 Step 3: Dismiss image extraction
  const handleDismissImageItems = async () => {
    await confirmAIAction(imageExtractCallId, 'ignored');
    setImageExtractedItems([]);
    setImageItemAssignees({});
    setImageExtractCallId(null);
    setImagePreview(null);
    toast.info('Discarded');
  };

  // HITL: Update an extracted task field
  const updateExtractedTask = (index, field, value) => {
    const updated = [...editedExtractedTasks];
    updated[index] = { ...updated[index], [field]: value };
    setEditedExtractedTasks(updated);
  };

  // HITL: Calculate edit delta between original and edited tasks
  const calculateExtractEditDelta = () => {
    const delta = [];
    editedExtractedTasks.forEach((edited, index) => {
      const original = extractedTasks[index];
      if (!original) return;
      ['child_name', 'task', 'category', 'due_date'].forEach(field => {
        if (edited[field] !== original[field]) {
          delta.push({
            field: `task_${index}_${field}`,
            original: String(original[field] || ''),
            corrected: String(edited[field] || '')
          });
        }
      });
    });
    return delta;
  };

  // HITL: Save all extracted tasks with confirmation
  const handleSaveAllExtractedTasks = async () => {
    const editDelta = calculateExtractEditDelta();
    const userAction = editDelta.length > 0 ? 'edited' : 'accepted';
    
    // Confirm the AI action
    await confirmAIAction(extractCallId, userAction, editDelta);
    
    // Save each task
    for (const task of editedExtractedTasks) {
      try {
        await axios.post(`${API}/school-tasks`, task);
      } catch (error) {
        console.error('Failed to save task:', error);
      }
    }
    
    toast.success(`${editedExtractedTasks.length} tasks saved!`);
    setExtractedTasks([]);
    setEditedExtractedTasks([]);
    setExtractCallId(null);
    setExtractText('');
    fetchTasks();
  };

  // HITL: Discard extracted tasks
  const handleDiscardExtractedTasks = async () => {
    await confirmAIAction(extractCallId, 'ignored');
    setExtractedTasks([]);
    setEditedExtractedTasks([]);
    setExtractCallId(null);
    toast.info('Extracted tasks discarded');
  };

  const saveExtractedTask = async (task, index) => {
    // Calculate delta for single task save
    const original = extractedTasks[index];
    const delta = [];
    if (original) {
      ['child_name', 'task', 'category', 'due_date'].forEach(field => {
        if (task[field] !== original[field]) {
          delta.push({
            field: `task_${index}_${field}`,
            original: String(original[field] || ''),
            corrected: String(task[field] || '')
          });
        }
      });
    }
    
    try {
      await axios.post(`${API}/school-tasks`, task);
      toast.success('Task saved!');
      
      // Remove from lists
      setExtractedTasks(prev => prev.filter((_, i) => i !== index));
      setEditedExtractedTasks(prev => prev.filter((_, i) => i !== index));
      
      // If last task, confirm the AI action
      if (extractedTasks.length === 1) {
        await confirmAIAction(extractCallId, delta.length > 0 ? 'edited' : 'accepted', delta);
        setExtractCallId(null);
      }
      
      fetchTasks();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  // AI: Generate uniform checklist with E2.6-B HITL + confidence
  const handleGenerateUniform = async () => {
    if (!uniformEvent.trim()) {
      toast.error('Please describe the event first');
      return;
    }
    setGeneratingUniform(true);
    setUniformLowConfidence(false);
    try {
      const response = await axios.post(`${API}/ai/school/kit-checklist`, {
        occasion: uniformEvent,
        child_name: filterChild !== 'all' ? filterChild : (children[0] || 'Child')
      });
      const checklist = (response.data.checklist || []).map(item => ({
        ...item,
        checked: false
      }));
      setUniformChecklist(checklist);
      setUniformCallId(response.data.call_id);
      setUniformLowConfidence(response.data.low_confidence || false);
    } catch (error) {
      // Fallback demo
      setUniformChecklist([
        { item: 'Clean sports uniform (white shirt, blue shorts)', checked: false },
        { item: 'White socks', checked: false },
        { item: 'Sports shoes (not sandals)', checked: false },
        { item: 'Water bottle', checked: false },
        { item: 'Towel', checked: false },
      ]);
      setUniformLowConfidence(true);
    } finally {
      setGeneratingUniform(false);
    }
  };

  // E2.6-B: Confirm uniform checklist HITL
  const confirmUniformChecklist = async (action) => {
    await confirmAIAction(uniformCallId, action);
    if (action === 'ignored') {
      setUniformChecklist([]);
    }
    setUniformCallId(null);
  };

  // AI: Generate tomorrow's prep
  const handleGenerateTomorrowPrep = async () => {
    setGeneratingPrep(true);
    setTomorrowPrepCallId(null);
    try {
      const response = await axios.post(`${API}/ai/tomorrow-prep`, {
        tasks: tomorrowTasks
      });
      const checklist = response.data.checklist || [];
      setTomorrowPrep(checklist);
      setEditedTomorrowPrep(JSON.parse(JSON.stringify(checklist)));
      setTomorrowPrepCallId(response.data.call_id || null);
    } catch (error) {
      // Generate based on actual tomorrow tasks
      const prepItems = tomorrowTasks.map(t => ({
        item: `${t.child_name}: ${t.task}`,
        category: t.category,
        checked: false
      }));
      if (prepItems.length === 0) {
        prepItems.push({ item: 'No specific tasks for tomorrow - regular school day!', category: 'general', checked: false });
      }
      setTomorrowPrep(prepItems);
      setEditedTomorrowPrep(JSON.parse(JSON.stringify(prepItems)));
      toast.success('Prep list generated!');
    } finally {
      setGeneratingPrep(false);
    }
  };

  // HITL: Update tomorrow prep item
  const updateTomorrowPrepItem = (index, field, value) => {
    const updated = [...editedTomorrowPrep];
    updated[index] = { ...updated[index], [field]: value };
    setEditedTomorrowPrep(updated);
  };

  // HITL: Use tomorrow prep list
  const handleUseTomorrowPrep = async () => {
    // Calculate delta
    const delta = [];
    editedTomorrowPrep.forEach((edited, index) => {
      const original = tomorrowPrep[index];
      if (original && edited.item !== original.item) {
        delta.push({
          field: `prep_${index}_item`,
          original: original.item || '',
          corrected: edited.item || ''
        });
      }
    });
    
    const userAction = delta.length > 0 ? 'edited' : 'accepted';
    await confirmAIAction(tomorrowPrepCallId, userAction, delta);
    
    toast.success('Prep list saved!');
    setTomorrowPrepCallId(null);
  };

  // HITL: Discard tomorrow prep
  const handleDiscardTomorrowPrep = async () => {
    await confirmAIAction(tomorrowPrepCallId, 'ignored');
    setTomorrowPrep([]);
    setEditedTomorrowPrep([]);
    setTomorrowPrepCallId(null);
    toast.info('Prep list discarded');
  };

  // AI: Generate reminder text
  const handleGenerateReminder = async (task) => {
    setGeneratingReminder(true);
    try {
      const response = await axios.post(`${API}/ai/school-reminder`, {
        task: task
      });
      setReminderText(response.data.reminder || '');
    } catch (error) {
      // Fallback demo
      setReminderText(`Hi! Just a friendly reminder about ${task.task} for ${task.child_name}. It's due ${task.due_date ? format(new Date(task.due_date), 'EEEE, MMMM d') : 'soon'}. Thank you!`);
      toast.success('Reminder generated! (Demo mode)');
    } finally {
      setGeneratingReminder(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Stats
  const completionRate = tasks.length > 0 
    ? Math.round((completedTasks.length / tasks.length) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-nurture-maize/20 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-nurture-maize" />
            </div>
            School Tracker
          </h1>
          <p className="text-nurture-muted mt-1">Keep track of all school-related tasks and events</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Child Filter */}
          {children.length > 0 && (
            <Select value={filterChild} onValueChange={setFilterChild}>
              <SelectTrigger className="w-[140px]" data-testid="child-filter">
                <User className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Children" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Children</SelectItem>
                {children.map(child => (
                  <SelectItem key={child} value={child}>{child}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-nurture-sage hover:bg-nurture-sage-hover text-white rounded-xl shadow-nurture-button" data-testid="add-task-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add School Task</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Child's Name</Label>
                  <Input
                    value={newTask.child_name}
                    onChange={(e) => setNewTask({...newTask, child_name: e.target.value})}
                    placeholder="Enter child's name"
                    required
                    data-testid="child-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Task</Label>
                  <Textarea
                    value={newTask.task}
                    onChange={(e) => setNewTask({...newTask, task: e.target.value})}
                    placeholder="What needs to be done?"
                    required
                    data-testid="task-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={newTask.category} onValueChange={(v) => setNewTask({...newTask, category: v})}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="due-date-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {newTask.due_date ? format(newTask.due_date, 'MMM d') : 'Select'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newTask.due_date}
                          onSelect={(date) => setNewTask({...newTask, due_date: date})}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Assign to (optional)</Label>
                  <Select value={newTask.assigned_to} onValueChange={(v) => setNewTask({...newTask, assigned_to: v})}>
                    <SelectTrigger data-testid="new-task-assignee-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {familyMembers.map(m => (
                        <SelectItem key={m.member_id || m.name} value={m.name}>
                          {m.name} ({m.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-nurture-muted">Who will actually do this. Separate from whoever spotted the notice.</p>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={newTask.notes}
                    onChange={(e) => setNewTask({...newTask, notes: e.target.value})}
                    placeholder="Additional details..."
                    data-testid="notes-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-nurture-sage hover:bg-nurture-sage-hover" data-testid="submit-task-btn">
                  Add Task
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-nurture-cream-dark/50 p-1 rounded-xl flex-wrap h-auto gap-1" data-testid="school-tabs">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-overview">
            <GraduationCap className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="inbox" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-inbox">
            <Inbox className="w-4 h-4 mr-2" />
            School Inbox
            <Badge className="ml-2 bg-nurture-terracotta/20 text-nurture-terracotta">AI</Badge>
          </TabsTrigger>
          <TabsTrigger value="forms" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-forms">
            <FileText className="w-4 h-4 mr-2" />
            Forms & Slips
          </TabsTrigger>
          <TabsTrigger value="uniform" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-uniform">
            <Shirt className="w-4 h-4 mr-2" />
            Uniform & Kit
          </TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-timeline">
            <Clock className="w-4 h-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="prep" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-prep">
            <Sparkles className="w-4 h-4 mr-2" />
            Tomorrow's Prep
            <Badge className="ml-2 bg-nurture-terracotta/20 text-nurture-terracotta">AI</Badge>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Overview */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-red-50 border-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">{overdueTasks.length}</p>
                    <p className="text-sm text-red-600">Overdue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-nurture-maize/20 border-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-8 h-8 text-nurture-maize" />
                  <div>
                    <p className="text-2xl font-bold text-nurture-navy">{todayTasks.length}</p>
                    <p className="text-sm text-nurture-muted">Due Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-nurture-sage/20 border-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-nurture-sage" />
                  <div>
                    <p className="text-2xl font-bold text-nurture-navy">{tomorrowTasks.length}</p>
                    <p className="text-sm text-nurture-muted">Tomorrow</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-nurture-cream-dark border-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-nurture-navy" />
                  <div>
                    <p className="text-2xl font-bold text-nurture-navy">{completionRate}%</p>
                    <p className="text-sm text-nurture-muted">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Task Lists */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Pending Tasks */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-nurture-terracotta" />
                  Pending Tasks ({pendingTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {pendingTasks.length === 0 ? (
                      <p className="text-center text-nurture-muted py-8">No pending tasks! Great job!</p>
                    ) : (
                      pendingTasks.map((task) => (
                        <motion.div
                          key={task.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`p-4 rounded-xl border ${
                            task.due_date && isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date))
                              ? 'bg-red-50 border-red-200'
                              : task.due_date && isToday(new Date(task.due_date))
                              ? 'bg-nurture-maize/10 border-nurture-maize/30'
                              : 'bg-white border-nurture-cream-dark'
                          }`}
                          data-testid={`task-${task.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={task.status === 'completed'}
                              onCheckedChange={() => toggleStatus(task)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">{task.child_name}</Badge>
                                <Badge className={`text-xs ${getCategoryStyle(task.category)}`}>
                                  {categories.find(c => c.value === task.category)?.label || task.category}
                                </Badge>
                              </div>
                              <p className="font-medium text-nurture-navy mt-1">{task.task}</p>
                              {task.due_date && (
                                <p className={`text-sm mt-1 ${
                                  isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date))
                                    ? 'text-red-500 font-medium'
                                    : 'text-nurture-muted'
                                }`}>
                                  {isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date)) 
                                    ? 'OVERDUE: ' 
                                    : isToday(new Date(task.due_date))
                                    ? 'TODAY: '
                                    : isTomorrow(new Date(task.due_date))
                                    ? 'TOMORROW: '
                                    : ''}
                                  {format(new Date(task.due_date), 'EEE, MMM d')}
                                </p>
                              )}
                              <div className="mt-2">
                                {canAssign ? (
                                  <Select
                                    value={task.assigned_to || UNASSIGNED}
                                    onValueChange={(v) => assignTask(task.id, v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs w-full max-w-[200px]" data-testid={`assignee-select-${task.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                                      {familyMembers.map(m => (
                                        <SelectItem key={m.member_id || m.name} value={m.name}>
                                          {m.name} ({m.role})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant="outline" className="text-xs" data-testid={`assignee-badge-${task.id}`}>
                                    {task.assigned_to ? `For ${task.assigned_to}` : 'Unassigned'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteTask(task.id)}
                              className="text-nurture-muted hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Completed Tasks */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-nurture-sage" />
                  Completed ({completedTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {completedTasks.length === 0 ? (
                      <p className="text-center text-nurture-muted py-8">No completed tasks yet</p>
                    ) : (
                      completedTasks.map((task) => (
                        <motion.div
                          key={task.id}
                          className="p-4 rounded-xl bg-nurture-sage/10 border border-nurture-sage/20"
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={true}
                              onCheckedChange={() => toggleStatus(task)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">{task.child_name}</Badge>
                              </div>
                              <p className="font-medium text-nurture-navy/60 line-through mt-1">{task.task}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteTask(task.id)}
                              className="text-nurture-muted hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2: School Inbox (AI) */}
        <TabsContent value="inbox" className="space-y-6">
          {/* Photo/PDF Upload Card - Phase 1 Step 3 */}
          <Card className="border-2 border-dashed border-nurture-terracotta/30 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-nurture-terracotta" />
                Upload School Letter/Photo
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta">AI Vision</Badge>
              </CardTitle>
              <p className="text-sm text-nurture-muted">
                Take a photo of any school letter, circular, or note. AI will extract tasks and deadlines.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* File Input */}
                <div className="flex-1">
                  <label 
                    htmlFor="school-image-upload" 
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-nurture-cream-dark rounded-xl cursor-pointer bg-white hover:bg-nurture-cream/50 transition-colors"
                  >
                    {imagePreview ? (
                      <div className="relative w-full h-full p-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-full object-contain rounded-lg"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1 rounded-full w-6 h-6 p-0"
                          onClick={(e) => {
                            e.preventDefault();
                            setImagePreview(null);
                            setImageExtractedItems([]);
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <ImageIcon className="w-8 h-8 text-nurture-muted mb-2" />
                        <p className="text-sm text-nurture-muted">
                          <span className="font-semibold text-nurture-terracotta">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-nurture-muted">PNG, JPG, PDF</p>
                      </div>
                    )}
                    <input 
                      id="school-image-upload" 
                      type="file" 
                      className="hidden" 
                      accept="image/*,application/pdf"
                      onChange={handleImageSelect}
                      data-testid="school-image-upload"
                    />
                  </label>
                </div>
                
                {/* Child Name + Extract Button */}
                <div className="flex flex-col gap-2 sm:w-48">
                  <Input
                    placeholder="Child's name"
                    value={imageChildName}
                    onChange={(e) => setImageChildName(e.target.value)}
                    className="rounded-xl"
                    data-testid="image-child-name"
                  />
                  <Button
                    onClick={handleExtractFromImage}
                    disabled={uploadingImage || !imagePreview}
                    className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover flex-1"
                    data-testid="extract-from-image-btn"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Extract
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Image Extracted Items - HITL Pattern */}
              {imageExtractedItems.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-nurture-terracotta/20">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-nurture-navy">
                      Extracted from Image ({imageExtractedItems.length})
                    </h3>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={handleSaveAllImageItems}
                        className="bg-nurture-sage hover:bg-nurture-sage-hover"
                        data-testid="save-all-image-items"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Save All
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={handleDismissImageItems}
                        data-testid="dismiss-image-items"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Dismiss
                      </Button>
                    </div>
                  </div>
                  
                  {imageExtractedItems.map((item, index) => (
                    <div key={index} className="p-4 rounded-xl bg-white border border-nurture-terracotta/20">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="secondary">{item.child_name || imageChildName || 'Child'}</Badge>
                            <Badge className={getCategoryStyle(item.category)}>{item.category || 'general'}</Badge>
                            {item.school_name && (
                              <Badge variant="outline" className="text-xs">{item.school_name}</Badge>
                            )}
                          </div>
                          <p className="font-medium text-nurture-navy">{item.item_text}</p>
                          {item.due_date && (
                            <p className="text-sm text-nurture-muted mt-1">
                              Due: {format(new Date(item.due_date), 'EEE, MMM d, yyyy')}
                            </p>
                          )}
                          <div className="mt-2">
                            <Label className="text-xs text-nurture-muted">Assign to</Label>
                            <Select
                              value={imageItemAssignees[index] || UNASSIGNED}
                              onValueChange={(v) => setImageItemAssignees(prev => ({ ...prev, [index]: v }))}
                            >
                              <SelectTrigger className="h-8 text-xs w-full max-w-[220px] mt-1" data-testid={`inbox-assignee-select-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                                {familyMembers.map(m => (
                                  <SelectItem key={m.member_id || m.name} value={m.name}>
                                    {m.name} ({m.role})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => saveImageExtractedItem(item, index)}
                          className="bg-nurture-sage hover:bg-nurture-sage-hover"
                          data-testid={`save-image-item-${index}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Text Paste Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-nurture-terracotta" />
                Paste Text Notice
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta">AI</Badge>
              </CardTitle>
              <p className="text-sm text-nurture-muted">
                Or paste any school notice, WhatsApp message, or email below.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={extractText}
                onChange={(e) => setExtractText(e.target.value)}
                placeholder="Paste school notice here...

Example:
Dear Parents,
Please be informed that Sports Day will be held on Friday, 15th December.
Students must wear their PE uniform and bring water bottles.
Permission slips must be submitted by Wednesday.
Thank you."
                className="min-h-[150px] font-mono text-sm"
                data-testid="extract-text-input"
              />
              <Button
                onClick={handleExtractTasks}
                disabled={extracting}
                className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white"
                data-testid="extract-tasks-btn"
              >
                {extracting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Extract Tasks with AI
                  </>
                )}
              </Button>

              {/* Extracted Tasks */}
              {extractedTasks.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-semibold text-nurture-navy">Extracted Tasks ({extractedTasks.length})</h3>
                  {extractedTasks.map((task, index) => (
                    <div key={index} className="p-4 rounded-xl bg-nurture-sage/10 border border-nurture-sage/30">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary">{task.child_name || 'Child'}</Badge>
                            <Badge className={getCategoryStyle(task.category)}>{task.category}</Badge>
                          </div>
                          <p className="font-medium text-nurture-navy">{task.task}</p>
                          {task.due_date && (
                            <p className="text-sm text-nurture-muted mt-1">
                              Due: {format(new Date(task.due_date), 'EEE, MMM d')}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => saveExtractedTask(task)}
                          className="bg-nurture-sage hover:bg-nurture-sage-hover"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Forms & Slips - E2.6-A Full CRUD */}
        <TabsContent value="forms" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-500" />
                    Forms & Permission Slips
                  </CardTitle>
                  <p className="text-sm text-nurture-muted mt-1">Track permission slips, fees, and forms</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={generateFormsReminder}
                    disabled={generatingReminder || schoolForms.filter(f => f.status === 'pending').length === 0}
                    data-testid="generate-forms-reminder-btn"
                  >
                    {generatingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span className="ml-2 hidden sm:inline">AI Reminder</span>
                  </Button>
                  <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-purple-500 hover:bg-purple-600" data-testid="add-form-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Form
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Form / Permission Slip</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddForm} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Child's Name *</Label>
                          <Input
                            value={newForm.child_name}
                            onChange={(e) => setNewForm({...newForm, child_name: e.target.value})}
                            placeholder="Enter child's name"
                            required
                            data-testid="form-child-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Form Title *</Label>
                          <Input
                            value={newForm.form_title}
                            onChange={(e) => setNewForm({...newForm, form_title: e.target.value})}
                            placeholder="e.g., Field Trip Permission, SPP Payment"
                            required
                            data-testid="form-title-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Amount (Rp)</Label>
                            <Input
                              type="number"
                              value={newForm.amount_rp}
                              onChange={(e) => setNewForm({...newForm, amount_rp: e.target.value})}
                              placeholder="0"
                              data-testid="form-amount-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Due Date</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {newForm.due_date ? format(newForm.due_date, 'MMM d') : 'Select'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={newForm.due_date}
                                  onSelect={(date) => setNewForm({...newForm, due_date: date})}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-purple-500 hover:bg-purple-600" data-testid="submit-form-btn">
                          Add Form
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* AI Reminder Output */}
                {reminderText && (
                  <div className="p-4 rounded-xl bg-nurture-maize/20 border border-nurture-maize/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-nurture-terracotta" />
                          <span className="text-sm font-medium text-nurture-navy">AI-Generated Reminder:</span>
                        </div>
                        <p className="text-nurture-navy whitespace-pre-line">{reminderText}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => {
                          navigator.clipboard.writeText(reminderText);
                          toast.success('Copied to clipboard!');
                          confirmAIAction(reminderCallId, 'accepted');
                        }}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        {/* Section 2 Fix: WhatsApp placeholder */}
                        <WhatsAppPlaceholder 
                          messageText={reminderText}
                          buttonLabel=""
                          size="sm"
                          variant="outline"
                          className="px-2"
                        />
                        <Button size="sm" variant="outline" onClick={() => {
                          setReminderText('');
                          confirmAIAction(reminderCallId, 'ignored');
                        }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Forms List */}
                {schoolForms.length === 0 ? (
                  <p className="text-center text-nurture-muted py-8">No forms added yet. Click "Add Form" to get started.</p>
                ) : (
                  <div className="space-y-3">
                    {schoolForms.map((form) => (
                      <div 
                        key={form.id} 
                        className={`p-4 rounded-xl border ${
                          form.status === 'pending' ? 'border-purple-200 bg-purple-50' :
                          form.status === 'paid' ? 'border-green-200 bg-green-50' :
                          'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="secondary">{form.child_name}</Badge>
                              <Badge className={
                                form.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                form.status === 'paid' ? 'bg-green-100 text-green-700' :
                                'bg-blue-100 text-blue-700'
                              }>
                                {form.status}
                              </Badge>
                              {form.amount_rp && (
                                <Badge variant="outline">Rp {form.amount_rp.toLocaleString()}</Badge>
                              )}
                            </div>
                            <p className="font-medium text-nurture-navy">{form.form_title}</p>
                            {form.due_date && (
                              <p className="text-sm text-nurture-muted mt-1">
                                Due: {format(new Date(form.due_date), 'EEE, MMM d, yyyy')}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {form.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:bg-green-50"
                                  onClick={() => updateFormStatus(form.id, form.amount_rp ? 'paid' : 'submitted')}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  {form.amount_rp ? 'Mark Paid' : 'Mark Done'}
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 hover:bg-red-50"
                              onClick={() => deleteForm(form.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: Uniform & Kit - E2.6-B with HITL + E2.6-E Confidence */}
        <TabsContent value="uniform" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shirt className="w-5 h-5 text-pink-500" />
                Uniform & Kit Checker
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta">AI</Badge>
              </CardTitle>
              <p className="text-sm text-nurture-muted">
                Describe an upcoming event and AI will generate a specific checklist
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Input
                  value={uniformEvent}
                  onChange={(e) => setUniformEvent(e.target.value)}
                  placeholder="e.g., Upacara bendera, Study tour ke museum, Swimming class..."
                  className="flex-1"
                  data-testid="uniform-event-input"
                />
                <Button
                  onClick={handleGenerateUniform}
                  disabled={generatingUniform}
                  className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover"
                  data-testid="generate-uniform-btn"
                >
                  {generatingUniform ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </Button>
              </div>

              {/* Checklist with HITL */}
              {uniformChecklist.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-nurture-navy">Checklist for: {uniformEvent}</h3>
                    {/* E2.6-E: Low Confidence Badge */}
                    {uniformLowConfidence && (
                      <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300" data-testid="low-confidence-badge">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        AI unsure - please verify
                      </Badge>
                    )}
                  </div>
                  
                  {uniformChecklist.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-pink-50">
                      <Checkbox
                        checked={item.checked}
                        onCheckedChange={() => {
                          const updated = [...uniformChecklist];
                          updated[index].checked = !updated[index].checked;
                          setUniformChecklist(updated);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <span className={item.checked ? 'line-through text-nurture-muted' : 'text-nurture-navy'}>
                          {item.item}
                        </span>
                        {item.quantity && (
                          <span className="text-sm text-nurture-muted ml-2">({item.quantity})</span>
                        )}
                        {item.notes && (
                          <p className="text-xs text-nurture-muted mt-1">{item.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* HITL Action Buttons */}
                  <div className="flex gap-2 pt-2 border-t border-pink-200">
                    <Button 
                      size="sm" 
                      className="bg-nurture-sage hover:bg-nurture-sage-hover"
                      onClick={() => {
                        const text = uniformChecklist.map(i => `${i.checked ? '✓' : '○'} ${i.item}${i.quantity ? ` (${i.quantity})` : ''}`).join('\n');
                        navigator.clipboard.writeText(text);
                        toast.success('Copied to clipboard!');
                        confirmUniformChecklist('accepted');
                      }}
                      data-testid="accept-uniform-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Use List
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        confirmUniformChecklist('ignored');
                        setUniformChecklist([]);
                      }}
                      data-testid="discard-uniform-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Discard
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 5: Timeline */}
        <TabsContent value="timeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-nurture-sage" />
                Child Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {children.length === 0 ? (
                <p className="text-center text-nurture-muted py-8">Add tasks with child names to see timelines</p>
              ) : (
                <Tabs defaultValue={children[0]} className="w-full">
                  <TabsList className="mb-4">
                    {children.map(child => (
                      <TabsTrigger key={child} value={child}>{child}</TabsTrigger>
                    ))}
                  </TabsList>
                  {children.map(child => {
                    const childTasks = tasks.filter(t => t.child_name === child);
                    const childPending = childTasks.filter(t => t.status === 'pending');
                    const childCompleted = childTasks.filter(t => t.status === 'completed');
                    const childProgress = childTasks.length > 0 
                      ? Math.round((childCompleted.length / childTasks.length) * 100) 
                      : 0;

                    return (
                      <TabsContent key={child} value={child} className="space-y-4">
                        <div className="flex items-center gap-4 p-4 rounded-xl bg-nurture-cream-dark/50">
                          <div className="flex-1">
                            <p className="text-sm text-nurture-muted mb-1">Overall Progress</p>
                            <Progress value={childProgress} className="h-2" />
                          </div>
                          <span className="text-lg font-bold text-nurture-navy">{childProgress}%</span>
                        </div>

                        {/* Timeline */}
                        <div className="relative pl-6 border-l-2 border-nurture-cream-dark space-y-4">
                          {childPending
                            .sort((a, b) => {
                              if (!a.due_date) return 1;
                              if (!b.due_date) return -1;
                              return new Date(a.due_date) - new Date(b.due_date);
                            })
                            .map((task, index) => (
                              <div key={task.id} className="relative">
                                <div className={`absolute -left-[25px] w-4 h-4 rounded-full ${
                                  task.due_date && isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date))
                                    ? 'bg-red-500'
                                    : task.due_date && isToday(new Date(task.due_date))
                                    ? 'bg-nurture-maize'
                                    : 'bg-nurture-sage'
                                }`} />
                                <div className="p-3 rounded-lg bg-white border border-nurture-cream-dark">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className={getCategoryStyle(task.category)}>
                                      {categories.find(c => c.value === task.category)?.label}
                                    </Badge>
                                    {task.due_date && (
                                      <span className="text-xs text-nurture-muted">
                                        {format(new Date(task.due_date), 'EEE, MMM d')}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-nurture-navy">{task.task}</p>
                                </div>
                              </div>
                            ))}
                          {childPending.length === 0 && (
                            <p className="text-nurture-muted py-4">No pending tasks for {child}</p>
                          )}
                        </div>
                      </TabsContent>
                    );
                  })}
                </Tabs>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 6: Tomorrow's Prep (AI) */}
        <TabsContent value="prep" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-nurture-terracotta" />
                Tomorrow's Prep
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta">AI</Badge>
              </CardTitle>
              <p className="text-sm text-nurture-muted">
                AI scans tomorrow's tasks and generates tonight's bag checklist
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={handleGenerateTomorrowPrep}
                disabled={generatingPrep}
                className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white"
                data-testid="generate-prep-btn"
              >
                {generatingPrep ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Tomorrow's Prep List
                  </>
                )}
              </Button>

              {/* Tomorrow Tasks Preview */}
              {tomorrowTasks.length > 0 && (
                <div className="p-4 rounded-xl bg-nurture-maize/10 border border-nurture-maize/30">
                  <h3 className="font-semibold text-nurture-navy mb-2">Tomorrow's Tasks ({tomorrowTasks.length})</h3>
                  <ul className="space-y-1">
                    {tomorrowTasks.map(task => (
                      <li key={task.id} className="text-sm text-nurture-navy flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-nurture-maize" />
                        <span className="font-medium">{task.child_name}:</span> {task.task}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Generated Prep List */}
              {tomorrowPrep.length > 0 && (
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="font-semibold text-nurture-navy">Tonight's Prep Checklist</h3>
                  {tomorrowPrep.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-nurture-sage/10">
                      <Checkbox
                        checked={item.checked}
                        onCheckedChange={() => {
                          const updated = [...tomorrowPrep];
                          updated[index].checked = !updated[index].checked;
                          setTomorrowPrep(updated);
                        }}
                      />
                      <span className={item.checked ? 'line-through text-nurture-muted' : 'text-nurture-navy'}>
                        {item.item}
                      </span>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(tomorrowPrep.map(i => `${i.checked ? '✓' : '○'} ${i.item}`).join('\n'))}>
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                    {/* Section 2 Fix: WhatsApp placeholder */}
                    <WhatsAppPlaceholder 
                      messageText={`Tonight's Prep Checklist:\n${tomorrowPrep.map(i => `${i.checked ? '✓' : '○'} ${i.item}`).join('\n')}`}
                      buttonLabel="Send to Helper"
                      size="sm"
                      variant="outline"
                      className="text-nurture-sage border-nurture-sage"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolTrackerPage;
